import { Moon, Sun } from 'lucide-react';
import { useTheme } from "next-themes";
import { Button } from "../Common/button";

import { useTranslation } from "react-i18next";

import { useAuthStore, useAuthActions } from "../../stores/authStore";
import { updateUserPreferences } from "../../services/userService";

interface ToggleThemeProps {
  iconOnly?: boolean;
}

export const ToggleTheme = ({ iconOnly = false }: ToggleThemeProps) => {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { updateUser } = useAuthActions();

  const handleToggle = async () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);

    if (user) {
      try {
        const updatedPrefs = await updateUserPreferences({ theme: newTheme });
        updateUser({ ...user, preferences: { ...user.preferences, ...updatedPrefs } });
      } catch (err) {
        console.error('Failed to save theme preference:', err);
      }
    }
  };

  if (iconOnly) {
    return (
      <Button
        onClick={handleToggle}
        size="icon"
        variant="ghost"
        className="h-9 w-9"
        title={t('theme.toggle')}
      >
        <Sun className="h-4 w-4 dark:hidden" />
        <Moon className="h-4 w-4 hidden dark:block" />
        <span className="sr-only">{t('theme.toggle')}</span>
      </Button>
    );
  }

  return (
    <Button
      onClick={handleToggle}
      size="sm"
      variant="ghost"
      className="w-full justify-start"
    >
      <div className="flex gap-2 dark:hidden">
        <Moon className="h-5 w-5" />
        <span className="block lg:hidden">{t('theme.dark')}</span>
      </div>

      <div className="dark:flex gap-2 hidden">
        <Sun className="h-5 w-5" />
        <span className="block lg:hidden">{t('theme.light')}</span>
      </div>

      <span className="sr-only">{t('theme.toggle')}</span>
    </Button>
  );
};
